import type { SetURLSearchParams } from "react-router-dom-v5-compat/dist/react-router-dom";

/**
 * Type for values that can be set in URL search parameters.
 * - string: Direct string value
 * - number: Numeric value (converted to string)
 * - boolean: Boolean value (converted to string)
 * - null | undefined: Removes the parameter from URL
 */
export type TUrlParameterValue = string | number | boolean | null | undefined;

/**
 * TODO: refactor to work with providing searchParams and setSearchParams each time
 * Custom hook for managing URL search parameters state.
 *
 * @param {string} key - The key of the URL search parameter to manage.
 * @param {URLSearchParams} searchParams - The current URL search parameters.
 * @param {SetURLSearchParams} setSearchParams - Function to update the URL search parameters.
 * @param {boolean} isReplaceUrl - Whether to replace the current URL (true) or push to history (false). Defaults to true.
 * @returns {[string, (value: TUrlParameterValue) => void]} - The current state and a function to update the state.
 *
 * @example
 * const [state, setState] = useUrlState('filter', searchParams, setSearchParams);
 *
 * // Get the current state
 * console.log(state); // Output: decoded value of 'filter' search param or ''
 *
 * // Set a new state
 * setState('newFilterValue');
 *
 * // Remove the state
 * setState(null); / setState(undefined);
 *
 * // Push to history instead of replacing
 * const [state, setState] = useUrlState('filter', searchParams, setSearchParams, false);
 */
export const useUrlState = (
  key: string,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  isReplaceUrl: boolean = true,
) => {
  const state = searchParams.get(key) ?? "";

  const setState = (value: TUrlParameterValue) => {
    setSearchParams(
      (prevSearchParams: URLSearchParams) => {
        if (value === undefined || value === null) prevSearchParams.delete(key);
        else prevSearchParams.set(key, String(value));
        return prevSearchParams;
      },
      { replace: isReplaceUrl },
    );
  };

  return [state, setState] as const;
};
